import { useId, type InputHTMLAttributes, type ReactNode } from 'react';

import { cn } from '@/lib/utils';

import { CampoFecha, type TipoFecha } from './calendario';

/** Tipos de fecha que dibuja el **calendario propio** en lugar del nativo. */
const TIPOS_FECHA = new Set<TipoFecha>(['date', 'datetime-local', 'month', 'time']);

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string;
  hint?: string;
  error?: string;
  leftIcon?: ReactNode;
  /** Contenido alineado a la derecha (ej. botón mostrar/ocultar contraseña). */
  rightSlot?: ReactNode;
  containerClassName?: string;
}

/**
 * Campo de texto con label, icono, ayuda y estado de error accesibles.
 * El `id` se genera automáticamente si no se recibe, de modo que label y
 * mensaje de error siempre queden asociados al input.
 */
export function Input({
  label,
  hint,
  error,
  leftIcon,
  rightSlot,
  containerClassName,
  className,
  id,
  disabled,
  ...rest
}: InputProps) {
  const autoId = useId();
  const inputId = id ?? `field-${autoId}`;
  const describedBy = error ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined;

  // Fechas y meses: se dibujan con el calendario propio del panel, no con el del
  // sistema operativo (que no se puede vestir). El valor sigue siendo
  // `AAAA-MM-DD`, `AAAA-MM-DDTHH:mm` o `AAAA-MM`.
  const tipoFecha = rest.type && TIPOS_FECHA.has(rest.type as TipoFecha) ? (rest.type as TipoFecha) : null;

  if (tipoFecha) {
    // El `onChange` de un `<input>` recibe un `ChangeEvent` completo; aquí sólo
    // existe el valor, así que se adapta (único punto donde hace falta).
    const alCambiar = rest.onChange
      ? (evento: { target: { value: string } }) =>
          (rest.onChange as unknown as (evento: { target: { value: string } }) => void)(evento)
      : undefined;

    return (
      <div className={cn('flex flex-col gap-1.5', containerClassName)}>
        {label ? (
          <label htmlFor={inputId} className="text-sm font-medium text-ink-700">
            {label}
          </label>
        ) : null}

        <div
          className={cn(
            'group relative flex items-center rounded-control border bg-white transition-all duration-200',
            'focus-within:ring-4',
            error
              ? 'border-rose-300 focus-within:border-rose-400 focus-within:ring-rose-100'
              : 'border-ink-200 focus-within:border-brand-400 focus-within:ring-brand-100',
            disabled && 'cursor-not-allowed bg-ink-50 opacity-70',
          )}
        >
          <CampoFecha
            id={inputId}
            name={rest.name}
            tipo={tipoFecha}
            value={rest.value === undefined || rest.value === null ? '' : String(rest.value)}
            onChange={alCambiar}
            disabled={disabled ?? false}
            placeholder={rest.placeholder}
            aria-describedby={describedBy}
            aria-invalid={Boolean(error) || undefined}
            className={cn(
              'h-12 w-full bg-transparent text-sm',
              leftIcon ? 'pl-11' : 'pl-4',
              rightSlot ? 'pr-12' : 'pr-4',
            )}
          />
        </div>

        {error ? (
          <p id={`${inputId}-error`} role="alert" className="text-xs font-medium text-rose-600">
            {error}
          </p>
        ) : hint ? (
          <p id={`${inputId}-hint`} className="text-xs text-ink-400">
            {hint}
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <div className={cn('flex flex-col gap-1.5', containerClassName)}>
      {label ? (
        <label htmlFor={inputId} className="text-sm font-medium text-ink-700">
          {label}
        </label>
      ) : null}

      <div
        className={cn(
          'group relative flex items-center rounded-control border bg-white transition-all duration-200',
          'focus-within:ring-4',
          error
            ? 'border-rose-300 focus-within:border-rose-400 focus-within:ring-rose-100'
            : 'border-ink-200 focus-within:border-brand-400 focus-within:ring-brand-100',
          disabled && 'cursor-not-allowed bg-ink-50 opacity-70',
        )}
      >
        {leftIcon ? (
          <span
            aria-hidden="true"
            className={cn(
              'pointer-events-none absolute left-3.5 flex items-center text-ink-400 transition-colors',
              'group-focus-within:text-brand-600',
              error && 'text-rose-400',
            )}
          >
            {leftIcon}
          </span>
        ) : null}

        <input
          id={inputId}
          disabled={disabled ?? false}
          aria-invalid={Boolean(error) || undefined}
          aria-describedby={describedBy}
          className={cn(
            'h-12 w-full bg-transparent text-sm text-ink-900 placeholder:text-ink-400',
            'outline-none disabled:cursor-not-allowed',
            leftIcon ? 'pl-11' : 'pl-4',
            rightSlot ? 'pr-12' : 'pr-4',
            className,
          )}
          {...rest}
        />

        {rightSlot ? <span className="absolute right-1.5 flex items-center">{rightSlot}</span> : null}
      </div>

      {error ? (
        <p id={`${inputId}-error`} role="alert" className="text-xs font-medium text-rose-600">
          {error}
        </p>
      ) : hint ? (
        <p id={`${inputId}-hint`} className="text-xs text-ink-400">
          {hint}
        </p>
      ) : null}
    </div>
  );
}
